import { Grid, Paper } from '@mui/material';
import { useSnackbar } from 'notistack';
import { useEffect, useState } from 'react';
import { SubmitHandler } from 'react-hook-form';
import { useMutation, useQuery } from 'react-query';
import { useNavigate, useParams } from 'react-router';
import { LocalUserForm } from '.';
import * as api from '../../api/app/localUserApi';
import { ApiError } from '../../components/core/ApiError/ApiError';
import { Loader } from '../../components/core/Loader/Loader';
import { PageContent } from '../../components/core/PageContent/PageContent';
import { PageHeader } from '../../components/core/PageHeader/PageHeader';
import {
  LocalUser,
  LocalUserInputs,
  LocalUserPasswordInputs,
} from '../../types/app/localUser.types';
import { dictionary } from '../../utils/core/dictionary';
import {
  convertDataFromInputToUpdateRequest,
  convertPassDataFromInputToUpdateRequest,
} from '../../utils/core/functions/localUser.functions';
import { paths } from '../../utils/core/routes';
import { useStyles } from './LocalUserForm.styles';
import { LocalUserPasswordChangeForm } from './LocalUserPasswordChangeForm';

export function LocalUserDetail() {
  const classes = useStyles();
  const { enqueueSnackbar } = useSnackbar();
  const navigate = useNavigate();
  const { id } = useParams();
  const [defaultValues, setDefaultValues] = useState<LocalUserInputs | null>(null);

  const { mutate: mutateUpdateLocalUser } = useMutation(api.updateItem);
  const { mutate: mutateUpdatePassword } = useMutation(api.updatePassword);
  const { data, isLoading, isError } = useQuery<LocalUser>(['localUser', id], () =>
    api.getItem(id!),
  );

  useEffect(() => {
    if (!data) return;

    setDefaultValues({
      id: data.id,
      name: data.name,
      email: data.email,
      password: data.password,
    });
  }, [data]);

  const handleOnSubmit: SubmitHandler<LocalUserInputs> = (data: LocalUserInputs) => {
    if (!defaultValues) return;

    const requestData = convertDataFromInputToUpdateRequest({
      ...data,
    });

    mutateUpdateLocalUser(
      { ...requestData, id: requestData.id },
      {
        onSuccess: () => {
          enqueueSnackbar(dictionary.LOCAL_USER_EDITED, { variant: 'success' });
          navigate(`${paths.LOCAL_USER}`);
        },
        onError: (result: any) => {
          enqueueSnackbar(`${result}`, { variant: 'error' });
        },
      },
    );
  };
  const handleOnSubmitPassword: SubmitHandler<LocalUserPasswordInputs> = (
    data: LocalUserPasswordInputs,
  ) => {
    if (!data.password) return;
    const requestData = convertPassDataFromInputToUpdateRequest({
      ...data,
      id: id!,
    });

    mutateUpdatePassword(
      { ...requestData, id: requestData.id, password: encodeURIComponent(requestData.password) },
      {
        onSuccess: () => {
          enqueueSnackbar(dictionary.LOCAL_USER_PASSWORD_CHANGED, { variant: 'success' });
          // navigate(`${paths.LOCAL_USER}`);
        },
        onError: (result: any) => {
          enqueueSnackbar(`${result}`, { variant: 'error' });
        },
      },
    );
  };

  if (isError) return <ApiError />;
  if (isLoading || !defaultValues) return <Loader />;

  return (
    <>
      <PageHeader title={dictionary.LOCAL_USER_DETAIL} />
      <PageContent>
        <Grid container spacing={3}>
          <Grid item xs={12} sm={6} lg={3}>
            <Paper className={classes.paper}>
              <LocalUserForm
                onSubmit={handleOnSubmit}
                onBack={() => navigate(`${paths.LOCAL_USER}`)}
                defaultValues={defaultValues}
                isDetail={true}
              />
            </Paper>
          </Grid>
          <Grid item xs={12} sm={6} lg={3}>
            <Paper className={classes.paper}>
              <LocalUserPasswordChangeForm onSubmit={handleOnSubmitPassword} />
            </Paper>
          </Grid>
        </Grid>
      </PageContent>
    </>
  );
}
